import type { DiscoveryV2ListResponseType } from "@sellar-io/api-v2/types";
import { Button, Card, H4, H5, H6 } from "@sellar-io/components";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useMediaQuery } from "usehooks-ts";
import { useShallow } from "zustand/react/shallow";
import { Icon, ProfilePicture, SpinnerLegacy } from "../../../../components";
import { IntersectingInfiniteLoadingButton } from "../../../../components/infiniteLoading/IntersectingInfiniteLoadingButton";
import { useDiscoveryV2ProductList } from "../../../../hooks/api/v2/discovery/useDiscoveryV2ProductList";
import { useFeatureFlagStore } from "../../../../hooks/useFeatureFlags";
import { useRemoteFeatureFlags } from "../../../../hooks/useRemoteFeatureFlags";
import { useDiscoveryV2SearchStore } from "../DiscoveryV2SearchStore";
import { useDiscoveryV2SearchProductListParams } from "./useDiscoveryV2SearchProductListParams";

type DiscoveryProduct = DiscoveryV2ListResponseType[number];
type ComparisonColumn = "popular" | "recommended";

const flattenProducts = (
  data: InfiniteData<DiscoveryV2ListResponseType> | undefined,
) => data?.pages.flat() ?? [];

const getRankByProductId = (products: DiscoveryProduct[]) => {
  const ranks = new Map<number, number>();
  products.forEach((product, index) => {
    ranks.set(product.id, index + 1);
  });
  return ranks;
};

export const DiscoverySortComparisonEntry = ({
  onStartComparison,
}: {
  onStartComparison: () => void;
}) => {
  const compareSortEnabled = useFeatureFlagStore().isActive(
    "DISCOVERY_SORT_COMPARISON",
  );

  if (!compareSortEnabled) return null;

  return (
    <Card
      className={twMerge(
        "bg-amber-50",
        "flex flex-row items-center justify-between gap-3 border border-amber-200 p-3",
      )}
    >
      <div className="min-w-0">
        <H5>Ranking comparison</H5>
        <H6>
          Internal testing tool. Compare ranking algorithms and category intent.
        </H6>
      </div>
      <Button color="slim" onClick={onStartComparison}>
        <Icon icon="COMPARE_HORIZONTAL" size={0.9} />
        Compare
      </Button>
    </Card>
  );
};

export const DiscoverySortComparison = ({ onExit }: { onExit: () => void }) => {
  const productListParams = useDiscoveryV2SearchProductListParams({
    limit: 20,
  });
  const recommendedSortEnabled = useRemoteFeatureFlags().isActive(
    "recommendedDiscoverySearch",
  );
  const [comparisonMode, setComparisonMode] = useState<"category" | "sort">(
    "category",
  );
  const categoryRankingEnabled = useRemoteFeatureFlags().isActive(
    "marketplaceCategoryIntentRanking",
  );
  const comparingCategory = comparisonMode === "category";
  const leftTitle = comparingCategory
    ? "Without category boost (Popular sort)"
    : "Popular (old)";
  const rightTitle = comparingCategory
    ? "With category boost (Popular sort)"
    : "Recommended (new)";
  const isDesktop = useMediaQuery("(min-width: 900px)");
  const [mobileColumn, setMobileColumn] =
    useState<ComparisonColumn>("recommended");

  const popularQuery = useDiscoveryV2ProductList({
    ...productListParams,
    sortBy: "popular",
    categoryIntentRanking: "disabled",
  });
  const recommendedQuery = useDiscoveryV2ProductList({
    ...productListParams,
    sortBy: comparingCategory ? "popular" : "recommended",
    categoryIntentRanking: comparingCategory ? undefined : "disabled",
  });

  const popularProducts = useMemo(
    () => flattenProducts(popularQuery.data),
    [popularQuery.data],
  );
  const recommendedProducts = useMemo(
    () => flattenProducts(recommendedQuery.data),
    [recommendedQuery.data],
  );
  const popularRanks = useMemo(
    () => getRankByProductId(popularProducts),
    [popularProducts],
  );
  const recommendedRanks = useMemo(
    () => getRankByProductId(recommendedProducts),
    [recommendedProducts],
  );
  const summary = useMemo(() => {
    let movedUp = 0;
    let movedDown = 0;
    let unchanged = 0;
    let entered = 0;

    for (const [productId, recommendedRank] of recommendedRanks) {
      const popularRank = popularRanks.get(productId);
      if (popularRank === undefined) {
        entered += 1;
        continue;
      }
      if (popularRank > recommendedRank) movedUp += 1;
      else if (popularRank < recommendedRank) movedDown += 1;
      else unchanged += 1;
    }

    return { movedUp, movedDown, unchanged, entered };
  }, [popularRanks, recommendedRanks]);

  const isLoading = popularQuery.isLoading || recommendedQuery.isLoading;

  return (
    <div className="flex min-h-0 flex-col gap-2">
      <Card className={twMerge("bg-amber-50", "border border-amber-200 p-4")}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <H4>Ranking comparison</H4>
            <H6>
              {comparingCategory
                ? "Same search, filters and Popular sort. Compare category boosting off and on."
                : "Same search and filters. Compare Popular with Recommended, with category boosting disabled on both sides."}
            </H6>
          </div>
          <Button color="slim" onClick={onExit}>
            Exit
          </Button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            color="slim"
            selected={comparingCategory}
            onClick={() => setComparisonMode("category")}
          >
            Category intent
          </Button>
          <Button
            color="slim"
            selected={!comparingCategory}
            onClick={() => setComparisonMode("sort")}
          >
            Popular vs Recommended
          </Button>
        </div>
        {!(comparingCategory
          ? categoryRankingEnabled
          : recommendedSortEnabled) && (
          <p className="mt-3 rounded-lg bg-white px-3 py-2 text-sm text-amber-900">
            {comparingCategory
              ? "Category boosting is off for this business. Enable marketplaceCategoryIntentRanking to compare it with the baseline. Queries without confident category intent will still have identical rankings."
              : "Recommended ranking is falling back to Popular. Enable recommendedDiscoverySearch for this business to compare the algorithms."}
          </p>
        )}
        {comparingCategory && (
          <H6 className="mt-3">
            Intent inference is skipped when style/category chips, an explicit
            category filter or boolean search expressions are active. Queries
            without confident category intent keep their existing order.
          </H6>
        )}
        {!isLoading && (
          <H6 className="mt-3">
            {[
              `${summary.movedUp} moved up`,
              `${summary.movedDown} moved down`,
              `${summary.unchanged} same rank`,
              `${summary.entered} new in ${comparingCategory ? "boosted" : "Recommended"} top ${recommendedProducts.length || 0}`,
            ].join(" · ")}
          </H6>
        )}
      </Card>
      {!isDesktop && (
        <div className="grid grid-cols-2 gap-2">
          <ColumnTab
            selected={mobileColumn === "popular"}
            onClick={() => setMobileColumn("popular")}
            label={leftTitle}
          />
          <ColumnTab
            selected={mobileColumn === "recommended"}
            onClick={() => setMobileColumn("recommended")}
            label={rightTitle}
          />
        </div>
      )}
      {isLoading ? (
        <SpinnerLegacy />
      ) : (
        <div
          className={twMerge(
            "grid items-start gap-2",
            isDesktop ? "grid-cols-2" : "grid-cols-1",
          )}
        >
          {(isDesktop || mobileColumn === "popular") && (
            <RankingColumn
              title={leftTitle}
              subtitle="Baseline ranking"
              query={popularQuery}
              products={popularProducts}
            />
          )}
          {(isDesktop || mobileColumn === "recommended") && (
            <RankingColumn
              title={rightTitle}
              subtitle={`Movement is versus ${leftTitle}`}
              query={recommendedQuery}
              products={recommendedProducts}
              comparisonRanks={popularRanks}
            />
          )}
        </div>
      )}
    </div>
  );
};

const ColumnTab = ({
  selected,
  onClick,
  label,
}: {
  selected: boolean;
  onClick: () => void;
  label: string;
}) => (
  <Button color="slim" selected={selected} className="w-full" onClick={onClick}>
    {label}
  </Button>
);

const RankingColumn = ({
  title,
  subtitle,
  query,
  products,
  comparisonRanks,
}: {
  title: string;
  subtitle: string;
  query: UseInfiniteQueryResult<InfiniteData<DiscoveryV2ListResponseType>>;
  products: DiscoveryProduct[];
  comparisonRanks?: Map<number, number>;
}) => {
  const { setSelectedProductIds, setSupplierId } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      setSelectedProductIds: state.setSelectedProductIds,
      setSupplierId: state.setSupplierId,
    })),
  );

  return (
    <div className="flex min-h-0 flex-col gap-2">
      <Card className={twMerge("bg-white", "sticky top-2 z-10 border p-3")}>
        <H5>{title}</H5>
        <H6>
          {subtitle}
          {products.length ? ` · ${products.length} loaded` : ""}
        </H6>
      </Card>
      {query.isError ? (
        <Card className={twMerge("bg-white", "border p-4")}>
          <H6>Couldn't load this ranking.</H6>
        </Card>
      ) : !products.length ? (
        <Card className={twMerge("bg-white", "border p-4")}>
          <H6>No results</H6>
        </Card>
      ) : (
        products.map((product, index) => (
          <ComparisonProductRow
            key={`${title}-${product.id}`}
            product={product}
            rank={index + 1}
            otherRank={comparisonRanks?.get(product.id)}
            showMovement={!!comparisonRanks}
            onClick={() => {
              setSupplierId(product.Supplier.id);
              setSelectedProductIds({
                productItemId: product.id,
                parentProductId: product.Parent?.id,
              });
            }}
          />
        ))
      )}
      <IntersectingInfiniteLoadingButton query={query} />
    </div>
  );
};

const ComparisonProductRow = ({
  product,
  rank,
  otherRank,
  showMovement,
  onClick,
}: {
  product: DiscoveryProduct;
  rank: number;
  otherRank: number | undefined;
  showMovement: boolean;
  onClick: () => void;
}) => {
  const [productName, productContainer] = product.name?.split(" • ") ?? [];

  return (
    <Card
      className={twMerge("bg-white", "cursor-pointer border")}
      onClick={onClick}
    >
      <div className="flex items-center gap-3 p-3">
        <span className="w-8 shrink-0 text-sm font-bold text-zinc-700 tabular-nums">
          {rank}
        </span>
        <ProfilePicture
          size="medium"
          logoUrl={product.Parent?.imageUrl}
          fallback="supplier-badge"
        />
        <div className="min-w-0 flex-1">
          <H5 className="truncate">{productName}</H5>
          <H6 className="truncate">
            {[
              product.Supplier.Business.name,
              product.Parent?.kind,
              productContainer,
            ]
              .filter(Boolean)
              .join(" · ")}
          </H6>
        </div>
        {showMovement && <RankMovement thisRank={rank} otherRank={otherRank} />}
      </div>
    </Card>
  );
};

const RankMovement = ({
  thisRank,
  otherRank,
}: {
  thisRank: number;
  otherRank: number | undefined;
}) => {
  if (otherRank === undefined) {
    return (
      <span className="shrink-0 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-800">
        not in Popular
      </span>
    );
  }

  const delta = otherRank - thisRank;
  if (delta === 0) {
    return (
      <span className="shrink-0 text-xs font-semibold text-zinc-500">same</span>
    );
  }

  if (delta > 0) {
    return (
      <span className="flex shrink-0 items-center gap-0.5 text-xs font-semibold text-emerald-700">
        <Icon icon="ARROW_UP" size={0.7} />
        {delta}
      </span>
    );
  }

  return (
    <span className="flex shrink-0 items-center gap-0.5 text-xs font-semibold text-red-700">
      <Icon icon="ARROW_DOWN" size={0.7} />
      {Math.abs(delta)}
    </span>
  );
};
